import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

import Button from '@/components/atoms/Button/Button';
import { CardContainer } from '@/components/templates/CardContainer/CardContainer';
import BaseField, { FieldProps } from '../BaseField/BaseField';
import { Option } from '../Select/Select';

interface TimePickerProps extends FieldProps<string> {
  value?: string;
  options: Option[];
  className?: string;
  onChange?: (value: string) => void;
}

export const TimePicker = ({
  value,
  options,
  className,
  onChange,
  id,
  label,
  description,
  tooltip,
  isRequired = false,
  isHideTitle = false,
}: TimePickerProps) => {
  const [selectedTime, setSelectedTime] = useState<string | undefined>(value);

  useEffect(() => {
    setSelectedTime(value);
  }, [value]);

  const handleTimeChange = useCallback(
    (option: string) => {
      if (onChange) {
        onChange(option);
      }
      setSelectedTime(option);
    },
    [onChange],
  );

  return (
    <BaseField
      id={id}
      className={className}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Stack
        sx={{
          gap: sp[16],
          p: sp[8],
          mb: sp[8],
          maxHeight: '45%',
          overflow: 'auto',
        }}
      >
        <CardContainer maxColumns={1} size='sm'>
          {options.map((option, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                color: selectedTime === option.value ? '#ffffff' : '#000000',
              }}
            >
              <Button
                ariaLabel={option.label}
                variant={
                  selectedTime === option.value ? 'primary' : 'secondary'
                }
                isRectangle
                onPress={() => handleTimeChange(option.value)}
              >
                {option.label}
              </Button>
            </Box>
          ))}
        </CardContainer>
      </Stack>
    </BaseField>
  );
};

TimePicker.displayName = 'TimePicker';

export default TimePicker;
